import React, {useCallback, useEffect, useMemo} from 'react';
import {useNavigate} from 'react-router-dom';
import {isEmpty} from 'lodash';
import ClearConfirmAlertPopup, {
  ClearConfirmPopupType,
} from './ClearConfirmAlertPopup';
import {useDispatch, useSelector} from 'react-redux';
import {RootState} from '../../../redux/store';
import {
  setShowModal,
  setTriggerLogout,
} from '../../../redux/slices/unSaveChangesModalSlice';
import {resetQuery} from '../../../redux/slices/querySlice';
import {
  GA_CLICK_TYPES,
  GA_EVENT_NAMES,
  GA_REGIONS,
  GA_SUBPAGE_TYPE,
} from '../../../constants/analyticsConstants';
import {t} from 'i18next';
import {useAnalytics} from '../../../hooks/useAnalytics';
import {EciHomePageTabs} from '../../../constants/constants';

interface LeaveSearchConfirmationDialogProps {
  searchQuery?: string;
  setQueryBuilderMode: (mode: boolean) => void;
  onLeavePageConfirm: () => void;
}

const LeaveSearchConfirmationDialog: React.FC<
  LeaveSearchConfirmationDialogProps
> = ({searchQuery, setQueryBuilderMode, onLeavePageConfirm}) => {
  const showModal = useSelector(
    (state: RootState) => state.unsavedChangesModal.showModal
  );
  const {builder} = useSelector((state: RootState) => state.query);

  const navigate = useNavigate();
  const dispatch = useDispatch();
  const {trackGAEvent} = useAnalytics();

  const closeDialog = useCallback(
    () => dispatch(setShowModal(false)),
    [dispatch]
  );
  const shouldTriggerAction = useSelector(
    (state: RootState) => state.unsavedChangesModal.shouldTriggerAction
  );
  useEffect(() => {
    closeDialog();
    if (!isEmpty(searchQuery)) {
      localStorage.setItem('currentPath', window.location.pathname);
    }

    const handleBeforeUnload = (event: BeforeUnloadEvent) => {
      event.preventDefault();
      event.returnValue = '';
    };

    const handlePopState = () => {
      localStorage.setItem(
        'currentLink',
        `/eci-home-page?tab=${EciHomePageTabs.ECI_OVERVIEW}`
      );
      // Handle back button (popstate event)

      trackGAEvent({
        eventName: GA_EVENT_NAMES.SUB_PAGE_VIEW,
        subPage: {
          subpage_name: t('gaTracking.global.unsavedChangesSubpageName'),
          subpage_type: GA_SUBPAGE_TYPE.MODAL,
        },
      });

      dispatch(setShowModal(true)); // Show custom modal
      window.history.pushState(null, '', window.location.pathname); // Prevent back navigation
    };

    // Add initial state to prevent back button navigation
    window.history.pushState(null, '', window.location.pathname);

    // Add event listeners for beforeunload and popstate
    window.addEventListener('beforeunload', handleBeforeUnload);
    window.addEventListener('popstate', handlePopState);

    // Cleanup event listeners when component unmounts
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
      window.removeEventListener('popstate', handlePopState);
    };
  }, [closeDialog, dispatch, searchQuery]);

  const onNavigatePage = useCallback(() => {
    const indexLink = localStorage?.getItem('currentLink');
    if (indexLink) {
      navigate(indexLink, {state: {source: 'nav-panel'}});
      localStorage?.removeItem('currentLink');
    } else {
      const link: string = localStorage?.getItem('currentPath') || '';
      navigate(link, {state: {source: 'nav-panel'}});
    }
    localStorage?.setItem('currentPath', '');
  }, [navigate]);

  const onLeavePage = useCallback(() => {
    closeDialog();
    dispatch(resetQuery());
    setQueryBuilderMode(false);
    shouldTriggerAction ? dispatch(setTriggerLogout()) : onNavigatePage();
    onLeavePageConfirm();
  }, [
    closeDialog,
    onNavigatePage,
    setQueryBuilderMode,
    shouldTriggerAction,
    dispatch,
    onLeavePageConfirm,
  ]);

  const onStayPage = useCallback(() => {
    localStorage?.removeItem('currentLink');
    closeDialog();

    // Track event on stay
    trackGAEvent({
      eventName: GA_EVENT_NAMES.CLICK,
      click: {
        name: t('gaTracking.queryBuilder.stayOnPage'),
        type: GA_CLICK_TYPES.BUTTON,
        region: GA_REGIONS.MODAL,
        action: t('gaTracking.global.closeUnsavedModal'),
      },
      subPage: {
        subpage_name: t('gaTracking.global.unsavedChangesSubpageName'),
        subpage_type: GA_SUBPAGE_TYPE.MODAL,
      },
    });
  }, [closeDialog]);

  const copiedText = useMemo(() => {
    return isEmpty(builder.rules) ? searchQuery : JSON.stringify(builder.rules);
  }, [builder.rules, searchQuery]);

  return (!isEmpty(searchQuery) || builder.rules.length) && showModal ? (
    <ClearConfirmAlertPopup
      data-testid={'clearConfirmAlertPopup'}
      queryText={copiedText}
      popupType={ClearConfirmPopupType.leaveSearchConfirm}
      onOk={onStayPage}
      onCancel={onLeavePage}
      showDialog={showModal}
      buttonStyle={'sideBySide'}
    />
  ) : null;
};

export default LeaveSearchConfirmationDialog;
